import worldCountriesText from './world-countries.geojson?raw';
import type { FeatureCollection, LayerManifest, SpatialFeature } from '../core/types';

const worldCountries = JSON.parse(worldCountriesText) as FeatureCollection;

export const countryLayer: LayerManifest = {
  id: 'countries',
  title: 'Countries',
  kind: 'geojson',
  visible: true,
  opacity: 0.92,
  interactive: true,
  data: worldCountries,
  style: {
    fillColor: '#12364f',
    outlineColor: '#2e789f',
    width: 0.65,
    clampToGround: true,
  },
  metadata: {
    source: 'Natural Earth 1:110m',
    stateType: 'observed',
    classification: 'public',
  },
};

export const nightLightsLayer: LayerManifest = {
  id: 'night-lights',
  title: 'Night Lights',
  kind: 'entities',
  visible: true,
  opacity: 0.72,
  interactive: false,
  data: makeNightLights(),
  style: { color: '#f7bd58', radius: 1.7 },
  metadata: { source: 'World Twin demo generator', stateType: 'synthetic', classification: 'public' },
};

export const globalEventLayer: LayerManifest = {
  id: 'global-events',
  title: 'Global Events',
  kind: 'entities',
  visible: true,
  opacity: 1,
  interactive: true,
  data: [
    point('evt-ukraine', 31.2, 49.1, 'Ukraine conflict', '#ff4d4f', 'critical'),
    point('evt-gaza', 34.47, 31.5, 'Gaza humanitarian crisis', '#ff6b3d', 'critical'),
    point('evt-iran', 53.7, 32.4, 'Iran tensions', '#ff8a34', 'high'),
    point('evt-chido', 42.2, -15.2, 'Cyclone Chido', '#2f80ff', 'high'),
    point('evt-canada', -120.4, 55.8, 'Wildfire cluster', '#ff8a34', 'warning'),
    point('evt-brazil', -52.2, -12.8, 'Flooding Brazil', '#f5c542', 'warning'),
  ],
  style: { color: '#ff4d4f', radius: 7, labelField: 'label', clampToGround: true },
  metadata: { source: 'World Twin demo feed', observedAt: '2025-05-14T00:00:00Z', stateType: 'observed', classification: 'public' },
};

export const mobilityLayer: LayerManifest = {
  id: 'mobility-routes',
  title: 'Global Mobility',
  kind: 'entities',
  visible: true,
  opacity: 0.9,
  interactive: true,
  data: [
    route('QTR732', [51.61, 25.27], [2.55, 49.01], '#28d7f2'),
    route('DAL456', [-73.78, 40.64], [-118.41, 33.94], '#2f80ff'),
    route('UAE789', [55.36, 25.25], [-0.45, 51.47], '#a86bf2'),
    route('AAL101', [-80.29, 25.79], [-3.57, 40.49], '#2f80ff'),
    route('SIA302', [103.99, 1.36], [8.57, 50.03], '#3ddc84'),
    route('BAW216', [-0.45, 51.47], [113.91, 22.31], '#f5c542'),
    route('AFR380', [2.55, 49.01], [28.25, -26.13], '#28d7f2'),
    route('ACA888', [-79.62, 43.68], [140.39, 35.77], '#a86bf2'),
  ],
  style: { color: '#2f80ff', width: 1.8, clampToGround: false },
  metadata: { source: 'World Twin trajectory simulator', stateType: 'synthetic', classification: 'public' },
};

export const infrastructureLayer: LayerManifest = {
  id: 'infrastructure-assets',
  title: 'Infrastructure Assets',
  kind: 'entities',
  visible: true,
  opacity: 1,
  interactive: true,
  data: [
    point('asset-sp', -46.63, -23.55, 'São Paulo energy hub', '#3ddc84', 'operational'),
    point('asset-rj', -43.17, -22.91, 'Rio transport hub', '#2f80ff', 'operational'),
    point('asset-bsb', -47.88, -15.79, 'Brasília telecom', '#28d7f2', 'operational'),
    point('asset-rec', -34.88, -8.05, 'Recife water system', '#f5c542', 'degraded'),
    point('asset-bel', -48.49, -1.46, 'Belém port', '#ff8a34', 'degraded'),
    point('asset-poa', -51.23, -30.03, 'Porto Alegre grid', '#ff4d4f', 'outage'),
  ],
  style: { radius: 6, labelField: 'label', clampToGround: true },
  metadata: { source: 'World Twin asset registry demo', stateType: 'observed', classification: 'internal' },
};

export const crisisLayer: LayerManifest = {
  id: 'crisis-zones',
  title: 'Crisis Zones',
  kind: 'geojson',
  visible: true,
  opacity: 0.8,
  interactive: true,
  data: {
    type: 'FeatureCollection',
    features: [
      circlePolygon('crisis-ukraine', 31.2, 49.1, 7.5, '#ff4d4f', 'Ukraine conflict'),
      circlePolygon('crisis-sudan', 30.4, 15.5, 6.5, '#ff4d4f', 'Sudan conflict'),
      circlePolygon('crisis-gaza', 34.47, 31.5, 2.4, '#ff6b3d', 'Gaza humanitarian crisis'),
      circlePolygon('crisis-dr-congo', 23.6, -2.8, 6, '#f5c542', 'DR Congo instability'),
    ],
  },
  style: { fillColor: '#ff4d4f', outlineColor: '#ff6b3d', width: 1.5, clampToGround: true },
  metadata: { source: 'Generalized demo geometry', stateType: 'inferred', classification: 'public' },
};

export const forecastLayer: LayerManifest = {
  id: 'hurricane-forecast',
  title: 'Hurricane Forecast',
  kind: 'entities',
  visible: true,
  opacity: 0.95,
  interactive: true,
  data: [
    {
      type: 'Feature',
      id: 'storm-track',
      properties: { name: 'Category 4 forecast track', color: '#ff4d4f', validFrom: '2025-05-20T00:00:00Z', validTo: '2025-05-25T00:00:00Z' },
      geometry: {
        type: 'LineString',
        coordinates: [[-55, 17], [-60, 19], [-65, 21], [-70, 23], [-75, 26], [-78, 29]],
      },
    },
    point('storm-center', -65, 21, 'Category 4', '#ff4d4f', 'forecast'),
  ],
  style: { color: '#ff4d4f', width: 3, radius: 9, labelField: 'label' },
  metadata: { source: 'World Twin forecast sandbox', stateType: 'forecast', classification: 'public' },
};

function point(id: string, longitude: number, latitude: number, label: string, color: string, status: string): SpatialFeature {
  return {
    type: 'Feature', id,
    properties: { label, name: label, color, status, observedAt: '2025-05-14T00:00:00Z' },
    geometry: { type: 'Point', coordinates: [longitude, latitude, 0] },
  };
}

function route(id: string, from: [number, number], to: [number, number], color: string): SpatialFeature {
  const coordinates: [number, number, number][] = [];
  const steps = 48;
  for (let index = 0; index <= steps; index += 1) {
    const t = index / steps;
    const longitude = from[0] + shortestLongitudeDelta(from[0], to[0]) * t;
    const latitude = from[1] + (to[1] - from[1]) * t + Math.sin(Math.PI * t) * 8;
    coordinates.push([normalizeLon(longitude), latitude, 25_000 + Math.sin(Math.PI * t) * 500_000]);
  }
  return {
    type: 'Feature', id,
    properties: { name: id, label: id, color, status: 'active', validFrom: '2025-05-14T00:00:00Z', validTo: '2025-05-27T00:00:00Z' },
    geometry: { type: 'LineString', coordinates },
  };
}

function circlePolygon(id: string, longitude: number, latitude: number, radiusDegrees: number, color: string, name: string): SpatialFeature {
  const ring: [number, number, number][] = [];
  for (let angle = 0; angle <= 360; angle += 10) {
    const radians = angle * Math.PI / 180;
    ring.push([
      longitude + Math.cos(radians) * radiusDegrees / Math.max(Math.cos(latitude * Math.PI / 180), 0.2),
      latitude + Math.sin(radians) * radiusDegrees,
      0,
    ]);
  }
  return { type: 'Feature', id, properties: { name, label: name, color }, geometry: { type: 'Polygon', coordinates: [ring] } };
}

function makeNightLights(): SpatialFeature[] {
  const cities: Array<[string, number, number, number]> = [
    ['New York', -74, 40.7, 8], ['Los Angeles', -118.2, 34.1, 6], ['São Paulo', -46.6, -23.5, 8],
    ['London', -0.1, 51.5, 7], ['Paris', 2.35, 48.86, 6], ['Lagos', 3.38, 6.52, 6],
    ['Cairo', 31.24, 30.04, 6], ['Johannesburg', 28.05, -26.2, 5], ['Mumbai', 72.88, 19.07, 7],
    ['Delhi', 77.1, 28.7, 8], ['Beijing', 116.4, 39.9, 7], ['Shanghai', 121.47, 31.23, 8],
    ['Tokyo', 139.69, 35.68, 9], ['Seoul', 126.98, 37.57, 7], ['Singapore', 103.82, 1.35, 6],
    ['Sydney', 151.21, -33.87, 5], ['Mexico City', -99.13, 19.43, 7], ['Istanbul', 28.98, 41.01, 6],
  ];
  return cities.flatMap(([name, lon, lat, intensity], cityIndex) => {
    const points: SpatialFeature[] = [];
    for (let index = 0; index < intensity; index += 1) {
      const angle = (index * 137.5 + cityIndex * 29) * Math.PI / 180;
      const radius = 0.25 + index * 0.16;
      points.push({
        type: 'Feature', id: `light-${cityIndex}-${index}`,
        properties: { name, color: index % 3 === 0 ? '#ffe2a1' : '#f7bd58' },
        geometry: { type: 'Point', coordinates: [lon + Math.cos(angle) * radius, lat + Math.sin(angle) * radius, 0] },
      });
    }
    return points;
  });
}

function shortestLongitudeDelta(from: number, to: number): number {
  const raw = to - from;
  return ((raw + 540) % 360) - 180;
}

function normalizeLon(value: number): number {
  return ((value + 540) % 360) - 180;
}
